import { DEFAULT_STRAP_DESIGN, audioSourceKey, defaultProject, editDurationMs, frameSize, isPieceSound, pieceSounds, sourceKey, type EditAudioSource, type EditProject, type EditSource, type SourceDurations } from '@airpost/shared';
import { MAX_DECODE_S, decodeSound, loadStill, sourceBlob } from './edit-media.js';
import { scheduleMix, type MixGraph } from './edit-mix.js';
import { deckMove, frameAt, gainAt, mixPlan, type Layer, type MixPlan } from './edit-plan.js';
import { FrameRenderer, inBox, renderMime, type Box, type LayerImage, type RenderResult } from './edit-render.js';
import { loadStrapArt } from './strap-art.js';

/**
 * The monitor of the editor: it plays the edit the way the export writes it. The pictures are drawn on a canvas of
 * the edit's format (its short side 720) from hidden video elements (one per source, two where consecutive pieces of
 * the same source meet, so a transition between them has both sides) and from the photos, frame by frame with
 * `frameAt` and the same `FrameRenderer` the export uses; the sound is the plan of `mixPlan` on a Web Audio graph,
 * and the audio clock is the clock of the edit. Levels are metered after the master. The same engine records the
 * edit in real time where the browser has no WebCodecs.
 */
export interface EngineUrls {
  video(source: EditSource): string;
  audio(source: EditAudioSource): string;
}
export interface EngineEvents {
  onTime?(ms: number): void;
  onPlaying?(playing: boolean): void;
  /** Peak levels after the master, left and right, in dBFS (−60 floor). */
  onLevels?(left: number, right: number): void;
  /** Playing but held: a picture that has to be seen is not loaded yet. */
  onWaiting?(waiting: boolean): void;
  /** A frame was drawn: where its texts and pictures are (for the selection's outline). */
  onDrawn?(boxes: Box[]): void;
}

const TICK_MS = 40;
/** The monitor's short side, whatever the export's resolution. */
const MONITOR_SHORT = 720;
/** How far ahead the next piece's element starts rolling (muted), so a cut does not flash black while it seeks. */
const PREROLL_MS = 600;
/** The longest the monitor holds for a picture before it plays on without it. */
const MAX_WAIT_MS = 8000;
const toDbfs = (v: number) => (v <= 0.001 ? -60 : Math.max(-60, 20 * Math.log10(v)));
/** Where an element's file ends, in seconds (unknown until its metadata is read). */
const fileEnd = (v: HTMLVideoElement) => (Number.isFinite(v.duration) ? v.duration : Number.POSITIVE_INFINITY);

export class EditEngine {
  private project: EditProject;
  private durations: SourceDurations = { video: () => null, audio: () => null };
  private plan: MixPlan;
  private readonly c: CanvasRenderingContext2D;
  private renderer: FrameRenderer;
  private readonly host: HTMLDivElement;
  private readonly decks = new Map<string, HTMLVideoElement>();
  private readonly deckUrls = new Map<string, string>();
  private deckOf: string[] = [];
  /** The photos, by source key: loaded, failed (null), or still loading (absent). */
  private readonly stills = new Map<string, LayerImage | null>();
  private audio: AudioContext | null = null;
  private bus: GainNode | null = null;
  private meters: [AnalyserNode, AnalyserNode] | null = null;
  private graph: MixGraph | null = null;
  private readonly buffers = new Map<string, AudioBuffer | null>();
  private playing = false;
  /** Bumped by every play and pause: a play that is still waiting for the audio device gives up when it changes. */
  private playToken = 0;
  private waitingSince: number | null = null;
  private readonly loading = new Set<string>();
  private startCtx = 0;
  private startSeqS = 0;
  private tMs = 0;
  private timer: number | null = null;
  private raf: number | null = null;
  /** When the screen's frame last drew the monitor, and when the outlines were last reported. */
  private paintedAt = 0;
  private outlinedAt = 0;
  private disposed = false;
  /** Recording a file: the monitor's frames are the file's, so a design's clock is left out. */
  private recording = false;
  private readonly meterData = new Float32Array(1024);

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly urls: EngineUrls,
    private readonly events: EngineEvents = {},
  ) {
    this.project = defaultProject({ kind: 'clip', id: '-' }, 40);
    const size = frameSize(this.project.format, MONITOR_SHORT);
    canvas.width = size.w;
    canvas.height = size.h;
    this.renderer = new FrameRenderer(size.w, size.h);
    this.c = canvas.getContext('2d')!;
    // the video elements play off-screen; only their pictures reach the canvas
    this.host = document.createElement('div');
    this.host.style.cssText = 'position:fixed;left:-10000px;top:0;width:4px;height:4px;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(this.host);
    this.plan = mixPlan(this.project);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  get time(): number {
    return this.tMs;
  }
  get isPlaying(): boolean {
    return this.playing;
  }
  get totalMs(): number {
    return editDurationMs(this.project);
  }
  /** The monitor's frame, in canvas pixels. */
  get size(): { w: number; h: number } {
    return { w: this.renderer.w, h: this.renderer.h };
  }

  /** A new project (every change of the edit): the frame follows the format, the decks the pieces; a playing mix is rescheduled from the playhead. */
  setProject(p: EditProject, durations: SourceDurations): void {
    this.project = p;
    this.durations = durations;
    this.plan = mixPlan(p, durations);
    const size = frameSize(p.format, MONITOR_SHORT);
    if (size.w !== this.renderer.w || size.h !== this.renderer.h) {
      this.canvas.width = size.w;
      this.canvas.height = size.h;
      this.renderer = new FrameRenderer(size.w, size.h);
    }
    this.assignDecks();
    this.loadStills();
    // the straps' design draws once its picture is in (a paused monitor is drawn again then)
    void loadStrapArt(p.strapDesign ?? DEFAULT_STRAP_DESIGN).then((img) => {
      if (img && !this.disposed && !this.playing && this.project === p) this.draw();
    });
    if (this.tMs > this.totalMs) this.tMs = this.totalMs;
    if (this.playing) this.reschedule();
    void this.loadBuffers();
    this.syncDecks();
    this.draw();
  }

  /**
   * Play from the playhead (from the start when it stands at the end). Playing starts at once: a sound still being
   * decoded joins the mix when it arrives, and until then the piece is heard from its video element.
   */
  async play(): Promise<void> {
    if (this.playing || this.disposed) return;
    if (this.tMs >= this.totalMs - 40) this.tMs = 0;
    const token = ++this.playToken;
    const ctx = this.ensureAudio();
    await ctx.resume();
    if (token !== this.playToken || this.playing || this.disposed) return;
    this.playing = true;
    this.waitingSince = null;
    // from the playhead as it stands now: the clock's last start belongs to the previous play
    this.reschedule(this.tMs);
    this.events.onPlaying?.(true);
    this.syncDecks();
    this.raf ??= requestAnimationFrame(this.paint);
    void this.loadBuffers();
  }

  pause(): void {
    this.playToken++;
    if (!this.playing) return;
    this.tMs = Math.min(this.clockMs(), this.totalMs);
    this.stop();
    this.events.onTime?.(this.tMs);
    this.draw();
  }

  toggle(): void {
    if (this.playing) this.pause();
    else void this.play();
  }

  seek(ms: number): void {
    this.tMs = Math.min(Math.max(0, ms), this.totalMs);
    if (this.playing) this.reschedule();
    this.syncDecks();
    this.events.onTime?.(this.tMs);
    this.draw();
  }

  /** A sound's decoded buffer, once loaded (for "normalise"). */
  bufferOf(source: EditAudioSource): AudioBuffer | null {
    return this.buffers.get(audioSourceKey(source)) ?? null;
  }

  /** The text, picture or piece drawn at a point of the canvas (topmost first), for picking it on the monitor. */
  boxAt(x: number, y: number): Box | null {
    const boxes = this.renderer.boxes;
    for (let i = boxes.length - 1; i >= 0; i--) if (inBox(boxes[i]!, x, y)) return boxes[i]!;
    return null;
  }

  /** Load what the edit sounds like: the pieces' sources and the clips' files, each decoded once; each joins a playing mix as it arrives. */
  async loadBuffers(): Promise<void> {
    const want = new Map<string, () => Promise<AudioBuffer | null>>();
    for (const s of this.project.segments) {
      // a photo and a frozen frame are silent, a piece whose sound was separated is heard from A3
      if (!pieceSounds(s)) continue;
      const key = sourceKey(s.source);
      const url = this.urls.video(s.source);
      if (!this.buffers.has(key) && !this.loading.has(key)) want.set(key, () => decodeSound(url));
    }
    for (const c of this.project.audio) {
      const key = audioSourceKey(c.source);
      const url = this.urls.audio(c.source);
      // a separated sound is its piece's file, read whole as the pieces are
      if (!this.buffers.has(key) && !this.loading.has(key)) want.set(key, () => decodeSound(url, { whole: !isPieceSound(c.source) ? false : undefined }));
    }
    if (!want.size) return;
    await Promise.all(
      [...want.entries()].map(async ([key, load]) => {
        this.loading.add(key);
        try {
          this.buffers.set(key, await load());
        } catch {
          this.buffers.set(key, null);
        } finally {
          this.loading.delete(key);
        }
        if (this.playing && !this.disposed) this.reschedule();
      }),
    );
  }

  /** Record the edit in real time from the canvas and the mix (the fallback where WebCodecs is missing). */
  async record(onProgress?: (fraction: number) => void): Promise<RenderResult> {
    const mime = renderMime();
    if (!mime) throw new Error('Ο browser δεν μπορεί να γράψει video');
    const ctx = this.ensureAudio();
    await ctx.resume();
    await this.loadBuffers();
    this.pause();
    this.seek(0);
    const dest = ctx.createMediaStreamDestination();
    this.bus!.connect(dest);
    const stream = this.canvas.captureStream(this.project.output.fps);
    for (const tr of dest.stream.getAudioTracks()) stream.addTrack(tr);
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 5_000_000, audioBitsPerSecond: 160_000 });
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    const stopped = new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
    });
    const total = this.totalMs;
    await loadStrapArt(this.project.strapDesign ?? DEFAULT_STRAP_DESIGN);
    this.recording = true;
    try {
      rec.start(500);
      await this.play();
      await new Promise<void>((resolve) => {
        const h = window.setInterval(() => {
          onProgress?.(Math.min(1, this.tMs / Math.max(1, total)));
          if (!this.playing) {
            window.clearInterval(h);
            resolve();
          }
        }, 100);
      });
      rec.stop();
      await stopped;
    } finally {
      this.recording = false;
    }
    this.bus!.disconnect(dest);
    const container = mime.split(';')[0]!;
    return { blob: new Blob(chunks, { type: container }), mime: container, durationMs: total };
  }

  destroy(): void {
    this.disposed = true;
    if (this.timer !== null) window.clearInterval(this.timer);
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.graph?.stop();
    for (const v of this.decks.values()) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    for (const u of this.deckUrls.values()) URL.revokeObjectURL(u);
    this.host.remove();
    void this.audio?.close();
  }

  // ── inside ──

  private ensureAudio(): AudioContext {
    if (!this.audio) {
      const ctx = new AudioContext({ latencyHint: 'playback' });
      const bus = ctx.createGain();
      const split = ctx.createChannelSplitter(2);
      const left = ctx.createAnalyser();
      const right = ctx.createAnalyser();
      left.fftSize = right.fftSize = 1024;
      bus.connect(ctx.destination);
      bus.connect(split);
      split.connect(left, 0);
      split.connect(right, 1);
      this.audio = ctx;
      this.bus = bus;
      this.meters = [left, right];
    }
    return this.audio;
  }

  private clockMs(): number {
    if (!this.playing || !this.audio) return this.tMs;
    return Math.max(0, (this.startSeqS + (this.audio.currentTime - this.startCtx)) * 1000);
  }

  /** Stop playing where the clock stands (the end, a pause): the mix and the elements stop with it. */
  private stop(): void {
    this.playing = false;
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.graph?.stop();
    this.graph = null;
    for (const v of this.decks.values()) v.pause();
    if (this.waitingSince !== null) {
      this.waitingSince = null;
      this.events.onWaiting?.(false);
      if (this.audio?.state === 'suspended') void this.audio.resume();
    }
    this.events.onPlaying?.(false);
  }

  /** The mix scheduled from a second of the sequence (the clock's when omitted), a moment ahead so every voice starts together. */
  private reschedule(fromMs?: number): void {
    const ctx = this.audio;
    if (!ctx || !this.bus) return;
    const from = fromMs ?? (this.playing ? this.clockMs() : this.tMs);
    this.graph?.stop();
    this.startCtx = ctx.currentTime + 0.06;
    this.startSeqS = from / 1000;
    this.tMs = from;
    this.graph = scheduleMix(ctx, this.plan, (key) => this.buffers.get(key) ?? null, this.startSeqS, this.startCtx, this.bus);
  }

  /** Which element plays each piece: consecutive pieces of one source alternate between two, so a transition has both; a photo has none. */
  private assignDecks(): void {
    const decks: string[] = [];
    this.project.segments.forEach((s, i) => {
      if (s.source.kind === 'image') {
        decks.push('');
        return;
      }
      const key = sourceKey(s.source);
      const prev = i > 0 ? this.project.segments[i - 1]! : null;
      const n = prev && sourceKey(prev.source) === key && decks[i - 1] === `${key}#0` ? 1 : 0;
      decks.push(`${key}#${n}`);
    });
    this.deckOf = decks;
    decks.forEach((d, i) => d && this.deck(d, this.project.segments[i]!.source));
  }

  private deck(id: string, source: EditSource): HTMLVideoElement {
    let v = this.decks.get(id);
    if (!v) {
      v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.preload = 'auto';
      v.crossOrigin = 'anonymous';
      // a piece at another speed sounds like the export: its pitch moves with it
      v.preservesPitch = false;
      this.host.appendChild(v);
      this.decks.set(id, v);
      const el = v;
      const url = this.urls.video(source);
      // the file is fetched once for the filmstrip; the element reads it from memory
      void sourceBlob(url)
        .then((blob) => {
          if (this.disposed) return;
          const objectUrl = URL.createObjectURL(blob);
          this.deckUrls.set(id, objectUrl);
          el.src = objectUrl;
          el.addEventListener('seeked', () => this.draw());
          el.addEventListener('loadeddata', () => {
            this.syncDecks();
            this.draw();
          });
        })
        .catch(() => {
          el.src = url;
        });
    }
    return v;
  }

  /** The photos of the edit (pieces and pictures over it), each loaded once. */
  private loadStills(): void {
    const want = new Map<string, EditSource>();
    for (const s of this.project.segments) if (s.source.kind === 'image') want.set(sourceKey(s.source), s.source);
    for (const o of this.project.overlays) want.set(sourceKey(o.source), o.source);
    for (const [key, source] of want) {
      if (this.stills.has(key) || this.loading.has(key)) continue;
      this.loading.add(key);
      void loadStill(this.urls.video(source))
        .then((img) => this.stills.set(key, img))
        .catch(() => this.stills.set(key, null))
        .finally(() => {
          this.loading.delete(key);
          if (!this.disposed) this.draw();
        });
    }
  }

  private layerDeck(l: Layer): HTMLVideoElement | null {
    const id = this.deckOf[l.index];
    return id ? (this.decks.get(id) ?? null) : null;
  }

  /**
   * Each element that has to be seen now plays (or stands) at its second, at its piece's speed; a frame that holds (a
   * frozen frame, a handle the source does not have, the file's end) stands on its frame; the element of a piece that
   * appears within PREROLL_MS already rolls, muted, so it is at its frame when it does; the rest pause.
   */
  private syncDecks(): void {
    const spec = frameAt(this.project, this.tMs, this.durations.video);
    const used = new Set<HTMLVideoElement>();
    const rolling = this.playing && this.waitingSince === null;
    const tS = this.tMs / 1000;
    for (const l of spec.layers) {
      const v = this.layerDeck(l);
      if (!v || v.readyState < 1) continue;
      used.add(v);
      const seg = this.project.segments[l.index]!;
      const { play: runs, atS: want } = deckMove(l, seg.speed, fileEnd(v), rolling);
      // a piece whose sound is not decoded (yet, or at all) is heard from its element; one whose sound was separated, never
      const key = sourceKey(seg.source);
      const decoded = this.buffers.get(key);
      const fallback = runs && pieceSounds(seg) && (decoded === undefined || (decoded === null && (v.duration || 0) <= MAX_DECODE_S * 4));
      v.muted = !fallback;
      if (fallback) {
        const voice = this.plan.voices.find((x) => x.kind === 'piece' && x.key === key && tS >= x.atS && tS < x.atS + x.durS);
        v.volume = Math.min(1, Math.max(0, gainAt(voice?.points ?? [], tS) * this.plan.tracks.video));
      }
      if (runs) {
        if (v.playbackRate !== seg.speed) v.playbackRate = seg.speed;
        if (Math.abs(v.currentTime - want) > 0.15 * Math.max(1, seg.speed)) v.currentTime = want;
        if (v.paused) void v.play().catch(() => undefined);
      } else {
        if (!v.paused) v.pause();
        if (Math.abs(v.currentTime - want) > 0.02) v.currentTime = want;
      }
    }
    if (rolling && this.tMs + PREROLL_MS < this.totalMs) {
      const ahead = frameAt(this.project, this.tMs + PREROLL_MS, this.durations.video);
      for (const l of ahead.layers) {
        const v = this.layerDeck(l);
        if (!v || used.has(v) || v.readyState < 1) continue;
        used.add(v);
        v.muted = true;
        const seg = this.project.segments[l.index]!;
        const { play, atS: want } = deckMove(l, seg.speed, fileEnd(v), true, PREROLL_MS);
        if (!play) {
          // a frame that will hold, or a handle that starts later, waits on its frame
          if (!v.paused) v.pause();
          if (Math.abs(v.currentTime - want) > 0.02) v.currentTime = want;
          continue;
        }
        if (v.playbackRate !== seg.speed) v.playbackRate = seg.speed;
        if (Math.abs(v.currentTime - want) > 0.15 * Math.max(1, seg.speed)) v.currentTime = want;
        if (v.paused) void v.play().catch(() => undefined);
      }
    }
    for (const v of this.decks.values()) if (!used.has(v) && !v.paused) v.pause();
  }

  /** A picture that has to be seen now and has nothing to show yet (its file still loading). */
  private stalled(): boolean {
    const spec = frameAt(this.project, this.tMs, this.durations.video);
    return spec.layers.some((l) => {
      const seg = this.project.segments[l.index];
      if (seg?.source.kind === 'image') return !this.stills.has(sourceKey(seg.source));
      const v = this.layerDeck(l);
      return !!v && !v.error && v.readyState < 2;
    });
  }

  /**
   * While playing, the monitor is drawn on every frame of the screen, not on the 25 a second of the tick, so a push, a
   * wipe or a dissolve moves evenly; the tick keeps the clock and the elements.
   */
  private readonly paint = (): void => {
    this.raf = null;
    if (!this.playing || this.disposed) return;
    this.tMs = Math.min(this.clockMs(), this.totalMs);
    this.draw();
    this.paintedAt = performance.now();
    this.raf = requestAnimationFrame(this.paint);
  };

  private draw(): void {
    const spec = frameAt(this.project, this.tMs, this.durations.video);
    this.renderer.render(this.c, {
      project: this.project,
      tMs: this.tMs,
      spec,
      imageFor: (l): LayerImage | null => {
        const v = this.layerDeck(l);
        return v && v.readyState >= 2 && v.videoWidth ? { img: v, w: v.videoWidth, h: v.videoHeight } : null;
      },
      stillFor: (key) => this.stills.get(key) ?? null,
      live: !this.recording,
    });
    // while playing, the outlines follow at the tick's pace, not the screen's
    const now = performance.now();
    if (this.playing && now - this.outlinedAt < TICK_MS) return;
    this.outlinedAt = now;
    this.events.onDrawn?.(this.renderer.boxes.map((b) => ({ ...b, x: Math.round(b.x), y: Math.round(b.y), w: Math.round(b.w), h: Math.round(b.h) })));
  }

  private tick(): void {
    if (this.disposed) return;
    if (this.playing) {
      // hold the clock (the audio device) while a picture that has to be seen is still loading
      const stalled = this.stalled();
      if (stalled && this.waitingSince === null) {
        this.waitingSince = performance.now();
        void this.audio?.suspend();
        this.events.onWaiting?.(true);
      } else if (this.waitingSince !== null && (!stalled || performance.now() - this.waitingSince > MAX_WAIT_MS)) {
        this.waitingSince = null;
        void this.audio?.resume();
        this.events.onWaiting?.(false);
      }
      const t = this.clockMs();
      if (t >= this.totalMs) {
        this.tMs = this.totalMs;
        this.stop();
      } else this.tMs = t;
      this.events.onTime?.(this.tMs);
      this.syncDecks();
      // the screen's frames draw the monitor; the tick only where there are none (a hidden page)
      if (performance.now() - this.paintedAt > TICK_MS * 2) this.draw();
    }
    if (this.meters && this.events.onLevels) {
      const peaks = this.meters.map((a) => {
        a.getFloatTimeDomainData(this.meterData);
        let p = 0;
        for (const v of this.meterData) p = Math.max(p, Math.abs(v));
        return toDbfs(this.playing ? p : 0);
      });
      this.events.onLevels(peaks[0]!, peaks[1]!);
    }
  }
}
